"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { priceInsightService } from "@/lib/services/priceInsightService";
import { mapPriceSuggestionToSellPriceBand } from "@/lib/pricing/mapPriceSuggestionToSellPriceBand";
import type { PricingDataSource } from "@/lib/pricing/pricingDataSource";
import {
  ADMIN_SELLER_BAND_CACHE_GEN,
  buildAdminSellerBandCacheKey,
} from "@/lib/pricing/pricingQueryIdentity";
import type { SellPriceBandSnapshot } from "@/lib/hooks/useSellPriceBand";
import type { PriceSuggestion } from "@/types/price-insight";

export { ADMIN_SELLER_BAND_CACHE_GEN };

type AdminSellerBandPreviewState = {
  fetchKey: string | null;
  band: SellPriceBandSnapshot | null;
  priceSuggestion: PriceSuggestion | null;
  isLoading: boolean;
  error: string | null;
  cachedAt: number | null;
};

const EMPTY: AdminSellerBandPreviewState = {
  fetchKey: null,
  band: null,
  priceSuggestion: null,
  isLoading: false,
  error: null,
  cachedAt: null,
};

type BandCacheEntry = {
  state: Omit<AdminSellerBandPreviewState, "isLoading">;
  cachedAt: number;
};

/** Admin review session — longer than seller flow; invalidated on listing fingerprint change. */
export const ADMIN_BAND_CACHE_TTL_MS = 30 * 60 * 1000;

const bandCache = new Map<string, BandCacheEntry>();

export function adminSellerBandCacheKey(
  title: string,
  category: string,
  subcategory: string,
  dataSource: PricingDataSource,
  excludeItemId?: string,
  listingAttributes?: Record<string, unknown> | null,
): string {
  return buildAdminSellerBandCacheKey({
    title,
    category,
    subcategory,
    dataSource,
    excludeItemId,
    listingAttributes,
  });
}

function isBandCacheFresh(entry: BandCacheEntry): boolean {
  return Date.now() - entry.cachedAt < ADMIN_BAND_CACHE_TTL_MS;
}

export type UseAdminSellerBandPreviewOptions = {
  enabled: boolean;
  title: string;
  category: string;
  subcategory: string;
  dataSource: PricingDataSource;
  debounceMs?: number;
  /** When set, this listing is omitted from SIO comp pool stats. */
  excludeItemId?: string;
  /**
   * Optional structured attrs for cache identity + request body.
   * Server also loads from `excludeItemId` when omitted.
   */
  listingAttributes?: Record<string, unknown> | null;
};

export function useAdminSellerBandPreview({
  enabled,
  title,
  category,
  subcategory,
  dataSource,
  debounceMs = 400,
  excludeItemId,
  listingAttributes,
}: UseAdminSellerBandPreviewOptions) {
  const [state, setState] = useState<AdminSellerBandPreviewState>(EMPTY);
  const requestIdRef = useRef(0);

  const trimmedTitle = title.trim();
  const trimmedCategory = category.trim();
  const ready =
    enabled && trimmedTitle.length >= 5 && trimmedCategory.length > 0;

  const fetchKey = ready
    ? adminSellerBandCacheKey(
        trimmedTitle,
        trimmedCategory,
        subcategory,
        dataSource,
        excludeItemId,
        listingAttributes,
      )
    : null;

  const applyCachedEntry = useCallback((key: string, entry: BandCacheEntry) => {
    setState({ ...entry.state, isLoading: false, cachedAt: entry.cachedAt });
  }, []);

  const runFetch = useCallback(
    async (options?: { force?: boolean }) => {
      if (!fetchKey || !ready) {
        setState(EMPTY);
        return;
      }

      const cached = bandCache.get(fetchKey);
      if (!options?.force && cached && isBandCacheFresh(cached)) {
        applyCachedEntry(fetchKey, cached);
        return;
      }

      const requestId = ++requestIdRef.current;

      setState((prev) => ({
        ...prev,
        fetchKey,
        isLoading: true,
        error: null,
      }));

      try {
        const suggestion = await priceInsightService.getPriceSuggestion(
          trimmedTitle,
          trimmedCategory,
          subcategory,
          undefined,
          { dataSource, excludeItemId, listingAttributes },
        );

        if (requestId !== requestIdRef.current) {
          return;
        }

        const band = mapPriceSuggestionToSellPriceBand(suggestion);
        const cachedAt = Date.now();
        const nextState: Omit<AdminSellerBandPreviewState, "isLoading"> = {
          fetchKey,
          band,
          priceSuggestion: suggestion,
          error: null,
          cachedAt,
        };

        bandCache.set(fetchKey, { state: nextState, cachedAt });
        setState({ ...nextState, isLoading: false });
      } catch (error) {
        if (requestId !== requestIdRef.current) {
          return;
        }
        const message =
          error instanceof Error ? error.message : "Failed to load market band";
        setState({
          fetchKey,
          band: null,
          priceSuggestion: null,
          isLoading: false,
          error: message,
          cachedAt: null,
        });
      }
    },
    [
      applyCachedEntry,
      dataSource,
      excludeItemId,
      fetchKey,
      listingAttributes,
      ready,
      subcategory,
      trimmedCategory,
      trimmedTitle,
    ],
  );

  useEffect(() => {
    if (!ready || !fetchKey) {
      return;
    }

    const cached = bandCache.get(fetchKey);
    if (cached && isBandCacheFresh(cached)) {
      const handle = window.setTimeout(
        () => applyCachedEntry(fetchKey, cached),
        0,
      );
      return () => window.clearTimeout(handle);
    }

    const handle = window.setTimeout(() => {
      void runFetch();
    }, debounceMs);

    return () => {
      window.clearTimeout(handle);
    };
  }, [ready, fetchKey, applyCachedEntry, runFetch, debounceMs]);

  const inactive = !ready || state.fetchKey !== fetchKey;

  return {
    band: inactive ? null : state.band,
    priceSuggestion: inactive ? null : state.priceSuggestion,
    isLoading: ready && state.isLoading,
    error: inactive ? null : state.error,
    cachedAt: inactive ? null : state.cachedAt,
    refresh: () => runFetch({ force: true }),
  };
}

/** Shared cache for batch price-suitability (table column). */
export function getCachedAdminSellerBand(
  title: string,
  category: string,
  subcategory: string,
  dataSource: PricingDataSource,
  listingAttributes?: Record<string, unknown> | null,
): SellPriceBandSnapshot | null {
  const key = adminSellerBandCacheKey(
    title,
    category,
    subcategory,
    dataSource,
    undefined,
    listingAttributes,
  );
  const cached = bandCache.get(key);
  if (!cached || !isBandCacheFresh(cached)) {
    return null;
  }
  return cached.state.band;
}

export function primeAdminSellerBandCache(
  title: string,
  category: string,
  subcategory: string,
  dataSource: PricingDataSource,
  band: SellPriceBandSnapshot,
  priceSuggestion: PriceSuggestion | null = null,
  listingAttributes?: Record<string, unknown> | null,
): void {
  const key = adminSellerBandCacheKey(
    title,
    category,
    subcategory,
    dataSource,
    undefined,
    listingAttributes,
  );
  const cachedAt = Date.now();
  bandCache.set(key, {
    state: {
      fetchKey: key,
      band,
      priceSuggestion,
      error: null,
      cachedAt,
    },
    cachedAt,
  });
}

/** Drop cached bands for a listing fingerprint (all data sources / attr variants). */
export function invalidateAdminSellerBandCacheForListing(params: {
  title: string;
  category: string;
  subcategory: string;
}): void {
  const titlePart = params.title.trim().toLowerCase();
  const categoryPart = params.category.trim();
  const subcategoryPart = params.subcategory;
  const needle = `|${categoryPart}|${subcategoryPart}|${titlePart}|`;

  for (const key of [...bandCache.keys()]) {
    if (key.includes(needle)) {
      bandCache.delete(key);
    }
  }
}

export function clearAdminSellerBandCache(): void {
  bandCache.clear();
}
